import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import FeedbackButtons from './FeedbackButtons';
import { Send, Loader2, Lock, Sparkles, User } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AIAssistant = () => {
  const { getAuthHeaders, subscription, refreshSubscription } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const messagesEndRef = useRef(null);

  const isLocked = !subscription?.ai_assistant;

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  useEffect(() => {
    if (!isLocked && messages.length === 0) {
      setMessages([{
        id: 'welcome',
        role: 'assistant',
        content: "Hi! I'm your content strategist. I can help you with:\n\n\u2022 Content planning and calendars\n\u2022 Keyword research ideas\n\u2022 SEO best practices\n\u2022 Writing tips and techniques\n\u2022 Platform-specific advice\n\nWhat would you like to work on today?"
      }]);
    }
  }, [isLocked]);

  const handleSend = async () => {
    if (!input.trim() || loading) return;
    const userMessage = input.trim();
    const userMsgId = `user-${Date.now()}`;
    setInput('');
    setMessages(prev => [...prev, { id: userMsgId, role: 'user', content: userMessage }]);
    setLoading(true);
    try {
      const response = await axios.post(`${API}/chat`, {
        message: userMessage, conversation_id: conversationId
      }, { headers: getAuthHeaders() });
      const assistantMsgId = `assistant-${Date.now()}`;
      setMessages(prev => [...prev, { id: assistantMsgId, role: 'assistant', content: response.data.response }]);
      setConversationId(response.data.conversation_id);
      await refreshSubscription();
    } catch (err) {
      setMessages(prev => [...prev, { id: `err-${Date.now()}`, role: 'assistant', content: err.response?.data?.detail || 'Sorry, I encountered an error. Please try again.' }]);
    } finally {
      setLoading(false);
    }
  };

  if (isLocked) {
    return (
      <div className="fade-in" data-testid="assistant-page-locked">
        <div className="text-center py-20">
          <div className="w-20 h-20 mx-auto rounded-full flex items-center justify-center mb-6 bg-[var(--color-surface)]">
            <Lock className="w-10 h-10 text-[var(--color-muted)]" />
          </div>
          <h1 className="font-serif text-3xl mb-3">AI Assistant</h1>
          <p className="text-sm text-[var(--color-muted)] max-w-md mx-auto mb-8">
            Your personal content strategist. Get advice on keywords, content planning, 
            SEO, and more. Available on Starter plan and above.
          </p>
          <button className="btn btn-accent">Upgrade to Starter (&#8364;29/mo)</button>
        </div>
      </div>
    );
  }

  return (
    <div className="fade-in h-[calc(100vh-8rem)] flex flex-col" data-testid="assistant-page">
      <div className="mb-4">
        <h1 className="font-serif text-3xl mb-1">AI Assistant</h1>
        <p className="text-sm text-[var(--color-muted)]">
          Your content strategist. Ask about keywords, strategy, SEO, and more. (1 credit per message)
        </p>
      </div>

      <div className="card flex-1 flex flex-col overflow-hidden">
        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {messages.map((message) => (
            <div key={message.id} className={`flex gap-3 ${message.role === 'user' ? 'flex-row-reverse' : ''}`}>
              <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${
                message.role === 'user' ? 'bg-[var(--color-charcoal)]' : 'bg-[var(--color-coral)]'
              }`}>
                {message.role === 'user' ? (
                  <User className="w-3.5 h-3.5 text-white" />
                ) : (
                  <Sparkles className="w-3.5 h-3.5 text-white" />
                )}
              </div>
              <div className="max-w-[80%]">
                <div className={`px-4 py-3 rounded-2xl text-sm leading-relaxed ${
                  message.role === 'user'
                    ? 'bg-[var(--color-charcoal)] text-white rounded-tr-md'
                    : 'bg-[var(--color-surface)] rounded-tl-md'
                }`}>
                  <p className="whitespace-pre-wrap">{message.content}</p>
                </div>
                {message.role === 'assistant' && message.id !== 'welcome' && (
                  <div className="mt-1 ml-1">
                    <FeedbackButtons targetType="chat" targetId={message.id} size="sm" />
                  </div>
                )}
              </div>
            </div>
          ))}
          {loading && (
            <div className="flex gap-3">
              <div className="w-7 h-7 rounded-full flex items-center justify-center bg-[var(--color-coral)]">
                <Sparkles className="w-3.5 h-3.5 text-white" />
              </div>
              <div className="px-4 py-3 rounded-2xl rounded-tl-md bg-[var(--color-surface)]">
                <Loader2 className="w-4 h-4 animate-spin text-[var(--color-coral)]" />
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        <div className="p-4 border-t border-[var(--color-border)]">
          <div className="flex gap-3">
            <input
              type="text" value={input} onChange={(e) => setInput(e.target.value)}
              onKeyPress={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
              placeholder="Ask about content strategy, keywords, SEO..."
              className="input flex-1" disabled={loading} data-testid="chat-input"
            />
            <button onClick={handleSend} disabled={loading || !input.trim()}
              className="btn btn-primary" data-testid="chat-send-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </button>
          </div>
          <p className="text-[10px] text-[var(--color-muted)] mt-2 text-center">
            Credits remaining: {subscription?.credits_remaining?.toFixed(1) || 0}
          </p>
        </div>
      </div>
    </div>
  );
};

export default AIAssistant;
